import { useMemo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { Badge, DeltaCell, DivergingBarCell, StackedBarCell, cn, type MetricDelta } from "@fox1ck-org/ui";
import { CampaignStatus, CostModel } from "@gen/tracker/v1/campaign_pb";
import { MetricDirection, MetricFormat } from "@gen/tracker/v1/reporting_pb";

import { DASH, formatChange, formatMoney, formatPp, relativeChange } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { ImportedBadge } from "@/components/campaign/imported";
import { formatMetric } from "@/lib/services/reporting-schema-service";

import { CampaignStatusPill } from "./status-pill";
import { OUTCOME, type Band, type ListColumn } from "./list-columns";
import type { Bag, Line } from "./list-rows";

/**
 * The campaign table's columns (boards CampList, CampGroups, Camp1920): the
 * name with the server's badges, the status word, then the bands. Every
 * figure is the server's value through the app's one formatter; the profit
 * bar is scaled to the largest profit on screen — a drawing, not a number.
 */

export function MetricValue({ c, v, strong }: { c: ListColumn; v: Bag; strong?: boolean }) {
  const def = c.def;
  const x = v[c.key];
  const signed = !!def?.emphasis;
  return (
    <span
      className={cn(
        "font-mono tabular-nums",
        c.dim && "text-muted-foreground",
        strong && "font-semibold",
        signed && x !== undefined && x > 0 && "text-ok",
        signed && x !== undefined && x < 0 && "text-destructive",
      )}
    >
      {def ? formatMetric(def, x) : DASH}
    </span>
  );
}

export function OutcomeBar({ v }: { v: Bag }) {
  const t = useT();
  return (
    <StackedBarCell
      placeholder={DASH}
      segments={[
        { value: v.conversions ?? 0, tone: "ok", label: t("cols.approved") },
        { value: v.conversions_hold ?? 0, tone: "neutral", label: t("cols.hold") },
        { value: v.rejected ?? 0, tone: "danger", label: t("cols.rejected") },
        { value: v.trash ?? 0, tone: "warn", label: t("cols.trash") },
      ]}
    />
  );
}

function ProfitBar({ c, v, max, strong }: { c: ListColumn; v: Bag; max: number; strong?: boolean }) {
  return (
    <DivergingBarCell value={v[c.key]} max={max || 1} placeholder={DASH} display={<MetricValue c={c} v={v} strong={strong} />} />
  );
}

/**
 * The change against the Top bar's comparison: relative for counts and
 * money, percentage points for rates, the difference for profit. `better`
 * is the schema's — spend going up is not good news.
 */
export function metricDelta(c: ListColumn, cur: number | undefined, prev: number | undefined): MetricDelta | undefined {
  if (cur === undefined || prev === undefined || !c.def) return undefined;
  const good = c.def.better === MetricDirection.LOWER ? "down" : "up";
  const direction = cur > prev ? "up" : cur < prev ? "down" : "flat";
  if (c.def.format === MetricFormat.PERCENT) return { value: formatPp(cur - prev), direction, good };
  if (c.def.emphasis) return { value: formatMoney(cur - prev, { signed: true }), direction, good };
  if (relativeChange(cur, prev) === undefined) return undefined;
  return { value: formatChange(cur, prev), direction, good };
}

const BAND_KEY: Record<Band, "camps.band.traffic" | "camps.band.conversions" | "camps.band.money" | "camps.band.custom"> = {
  traffic: "camps.band.traffic",
  conversions: "camps.band.conversions",
  money: "camps.band.money",
  custom: "camps.band.custom",
};

export function useCampaignColumns(opts: {
  columns: readonly ListColumn[];
  withDelta: boolean;
  profitMax: number;
  /** Board Camp1920: the source and the owner beside the name. */
  wide: boolean;
}): ColumnDef<Line, unknown>[] {
  const t = useT();
  const { columns, withDelta, profitMax, wide } = opts;
  return useMemo(() => {
    const metric = (c: ListColumn): ColumnDef<Line, unknown> => ({
      id: c.key,
      accessorFn: (r) => r.v[c.key] ?? null,
      enableSorting: c.key !== OUTCOME,
      size: c.key === OUTCOME ? 120 : c.def?.emphasis && c.key === "profit" ? 132 : 72,
      meta: { align: "right" },
      header: () => (
        <span className="whitespace-nowrap" title={[c.label, c.description].filter(Boolean).join(": ")}>
          {c.short}
        </span>
      ),
      cell: ({ row }) => {
        const r = row.original;
        const strong = r.kind === "group";
        if (c.key === OUTCOME) return <OutcomeBar v={r.v} />;
        if (c.key === "profit") return <ProfitBar c={c} v={r.v} max={profitMax} strong={strong} />;
        return <MetricValue c={c} v={r.v} strong={strong} />;
      },
    });
    const delta = (c: ListColumn): ColumnDef<Line, unknown> => ({
      id: `${c.key}__d`,
      enableSorting: false,
      size: 64,
      meta: { align: "right" },
      header: () => <span title={t("camps.delta.hint")}>Δ</span>,
      cell: ({ row }) => <DeltaCell delta={metricDelta(c, row.original.v[c.key], row.original.prev?.[c.key])} />,
    });
    const bands = (["traffic", "conversions", "money", "custom"] as const).flatMap((band) => {
      const leaves = columns
        .filter((c) => c.band === band)
        .flatMap((c) => (withDelta && c.key !== OUTCOME ? [metric(c), delta(c)] : [metric(c)]));
      return leaves.length > 0 ? [{ id: `band-${band}`, header: t(BAND_KEY[band]), columns: leaves }] : [];
    });
    return [
      {
        id: "band-name",
        header: "",
        columns: [
          {
            id: "name",
            header: t("camps.col.name"),
            accessorFn: (r: Line) => r.name,
            size: wide ? 340 : 260,
            cell: ({ row }) => <NameCell line={row.original} wide={wide} />,
          },
          {
            id: "status",
            header: t("camps.col.status"),
            enableSorting: false,
            size: 96,
            cell: ({ row }) => (row.original.kind === "campaign" ? <CampaignStatusPill status={row.original.item.campaign?.status} /> : null),
          },
        ],
      },
      ...bands,
    ];
  }, [columns, withDelta, profitMax, wide, t]);
}

function NameCell({ line, wide }: { line: Line; wide: boolean }) {
  const t = useT();
  if (line.kind === "group") {
    return (
      <span className="flex min-w-0 items-baseline gap-2">
        <b className="truncate font-semibold">{line.name}</b>
        <span className="shrink-0 text-xs text-muted-foreground">{t("camps.group.count", { count: line.campaigns })}</span>
      </span>
    );
  }
  const flags = line.item.flags;
  const manual = line.item.campaign?.costModel === CostModel.MANUAL;
  const meta = wide ? [line.item.trafficSourceName, line.item.ownerName].filter(Boolean).join(" · ") : "";
  const badges: ReactNode[] = [];
  if (flags.includes("losing")) badges.push(<Badge key="l" variant="danger" size="sm">{t("camps.badge.losing")}</Badge>);
  if (flags.includes("no_clicks")) badges.push(<Badge key="n" variant="warn" size="sm">{t("camps.badge.noClicks")}</Badge>);
  if (manual) badges.push(<Badge key="m" variant="info" size="sm">{t("camps.badge.manualCost")}</Badge>);
  // A width, not just min-w-0: the table lays out by content, and a long
  // name with a badge would otherwise push ROI off a MacBook's screen.
  return (
    <span
      className={cn(
        "flex min-w-0 items-center gap-2",
        wide ? (line.nested ? "w-79 pl-6" : "w-85") : line.nested ? "w-59 pl-6" : "w-65",
      )}
    >
      <Link
        to={`/campaigns/${line.campaignId}`}
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "truncate font-medium hover:text-primary",
          line.item.campaign?.status === CampaignStatus.ACTIVE ? "text-foreground" : "text-muted-foreground",
        )}
        title={line.name}
      >
        {line.name}
      </Link>
      {meta ? <span className="min-w-0 shrink truncate text-xs text-muted-foreground">{meta}</span> : null}
      {badges.length > 0 ? <span className="flex shrink-0 gap-1">{badges}</span> : null}
      <ImportedBadge campaign={line.item.campaign} size="sm" />
    </span>
  );
}

/** «Итого» under the table: the server's totals of every matching campaign. */
export function totalsRow(opts: {
  label: ReactNode;
  columns: readonly ListColumn[];
  v: Bag | undefined;
  prev: Bag | undefined;
  withDelta: boolean;
  profitMax: number;
}): Record<string, ReactNode> | undefined {
  const { v, prev } = opts;
  if (!v) return undefined;
  const out: Record<string, ReactNode> = { name: <span className="font-semibold">{opts.label}</span> };
  for (const c of opts.columns) {
    out[c.key] =
      c.key === OUTCOME ? (
        <OutcomeBar v={v} />
      ) : c.key === "profit" ? (
        <ProfitBar c={c} v={v} max={Math.max(opts.profitMax, Math.abs(v.profit ?? 0))} strong />
      ) : (
        <MetricValue c={c} v={v} strong />
      );
    if (opts.withDelta && c.key !== OUTCOME) out[`${c.key}__d`] = <DeltaCell delta={metricDelta(c, v[c.key], prev?.[c.key])} />;
  }
  return out;
}
